import axios from 'axios';
import {
	API_ENDPOINTS,
	Button,
	ButtonTypes,
	Card,
	CardBody,
	customToast,
	Dropdown,
	Icons,
	Modal,
	PageLayout,
	replaceKeyWithValue,
	route,
	StringHelpers,
	SubTitle,
	useDisabledContext,
	useLangContext,
	useStore,
	Variants,
	WidthConstrainedContainer,
	FormInput,
	DateFormats,
	Sizes,
	checkTheme,
	Themes,
} from 'carrier-fe';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EventType } from '../../../types/event';
import EventInvites from './EventInvites';
import { DropdownItemProps } from 'carrier-fe/lib/components/Controls/Dropdown/DropdownItem/DropdownItem';
import { formatDate, isFuture, parse } from 'date-fns';
import { SEARCH_FILTERS } from '../../../Constants';

function Event() {
	const { eventId } = useParams();

	const { crud, fields, models } = useLangContext();
	const { disabled, setDisabled } = useDisabledContext();
	const navigate = useNavigate();

	const { store, setValues } = useStore<EventType>({} as EventType);

	const [archiveModalOpen, setArchiveModalOpen] = useState<boolean>(false);
	const [archiving, setArchiving] = useState<boolean>(false);
	const [completeModalOpen, setCompleteModalOpen] = useState<boolean>(false);

	const [refundIfPaid, setRefundIfPaid] = useState<boolean>(false);

	const multiDayEvent = store?.end_date !== store?.event_at;

	useEffect(() => {
		fetchEventData();
	}, [eventId]);

	useEffect(() => {
		if (archiving) {
			archive();
		}
	}, [archiving]);

	const fetchEventData = useCallback(async () => {
		try {
			const { data } = await axios.get<{ data: EventType }>(
				route(API_ENDPOINTS.ADMIN.TRAINING.EVENT.VIEW, {
					trainingEvent: String(eventId),
				})
			);
			setValues(data.data);

			let eventAt = parse(data.data.event_at, DateFormats.DB, new Date());

			if (isFuture(eventAt)) {
				setRefundIfPaid(true);
			} else {
				setRefundIfPaid(false);
			}
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'An error occurred. Please try again.',
				variant: Variants.Danger,
			});
		}
	}, [eventId]);

	const archive = async () => {
		setDisabled(true);

		try {
			const { data } = await axios.delete(
				route(API_ENDPOINTS.ADMIN.TRAINING.EVENT.DELETE, {
					trainingEvent: String(eventId),
				}),
				{
					data: {
						refund_if_paid: refundIfPaid,
					},
				}
			);
			customToast({
				title:
					data.message ||
					replaceKeyWithValue(
						'Success. Data for :model has been archived.',
						'model',
						models?.training_event || 'event(s)'
					),
				variant: Variants.Success,
			});

			navigate('/training/event');
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'An error occurred. Please try again.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
			setArchiving(false);
			setArchiveModalOpen(false);
		}
	};

	const getActions = (): DropdownItemProps[] => {

		let actions: DropdownItemProps[] = [];

		if (store?.permissions?.update) {
			if (!store?.completed_at) {
				actions.push({
					label: StringHelpers.title(
						crud?.buttons.edit.default || 'Update'
					),
					onClick: () => navigate(`/training/event/${eventId}/edit`),
					variant: Variants.Dark,
					icon: Icons.EDIT,
					disabled: disabled,
				});
			}

			if (store?.can_be_completed && store.attendee_count > 0) {
				actions.push({
					label: StringHelpers.title(
						crud?.buttons.complete.default || 'Complete'
					),
					onClick: () => checkTheme(Themes.Toshiba)
						? setCompleteModalOpen(true)
						: navigate(`/training/event/${eventId}/complete`),
					variant: Variants.Success,
					icon: Icons.CHECK_CIRCLE,
					disabled: disabled,
				});
			}
		}

		if (store?.permissions?.delete) {
			actions.push({
				label: StringHelpers.title(
					crud?.buttons.archive.default || 'Archive'
				),
				onClick: () => setArchiveModalOpen(true),
				variant: Variants.Danger,
				icon: Icons.DELETE,
				disabled,
			});
		}

		return actions;
	};

	let title = replaceKeyWithValue(
		crud?.pages?.view?.title || 'View :model',
		'model',
		crud?.models?.training_event || 'Training Event'
	);

	let actions = getActions();

	return (
		<PageLayout title={StringHelpers.title(title)}>
			<WidthConstrainedContainer>
				<div className="mb-4 d-flex justify-content-between align-items-center">
					<Button
						label={StringHelpers.title(
							crud?.buttons?.back?.default || 'Back'
						)}
						onClick={() => {
							const savedFilter = sessionStorage.getItem(SEARCH_FILTERS.EVENT);
              				sessionStorage.removeItem(SEARCH_FILTERS.EVENT);
							navigate(
								savedFilter
									? `/training/event?filter[search]=${encodeURIComponent(savedFilter)}`
									: '/training/event'
							);
						}}
						variant={Variants.Dark}
						type={ButtonTypes.Outline}
						disabled={disabled}
					/>
					{actions?.length > 0 && (
						<Dropdown
							label={StringHelpers.title(
								crud?.buttons?.actions?.default || 'Actions'
							)}
							items={actions}
							type={ButtonTypes.Solid}
							variant={Variants.Dark}
							disabled={disabled}
						/>
					)}
				</div>
				<Card>
					<CardBody>
						<SubTitle
							title={StringHelpers.title(
								crud?.sub_titles?.details || 'Details'
							)}
							className="mb-3"
							style={{ color: '#464C5E' }}
						/>
						<div className="row mb-4">
							<div className="col-12 col-lg-6 col-xxl-4">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.name || 'Name'
										)}
									</strong>
									<div>{store?.name || '-'}</div>
								</div>
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.description || 'Description'
										)}
									</strong>
									<div>{store?.description || '-'}</div>
								</div>
							<div className="d-flex flex-column mb-3">
								<strong>
									{StringHelpers.title(
										checkTheme(Themes.Toshiba)
											? fields?.training_courses || 'Courses'
											: fields?.training_course || 'Course'
									)}
								</strong>
								<div className="d-flex flex-column">
									{store?.training_courses?.length > 0
										? store.training_courses.map((course) =>
											course.permissions?.view ? (
												<a
													key={course.id}
													className={'text-dark'}
													href={`/training/course/${course.id}`}
													target={'_blank'}
												>
													{course.name || '-'}
												</a>
											) : (
												<span key={course.id}>
													{course.name || '-'}
												</span>
											)
										)
										: '-'}
								</div>
							</div>
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.capacity || 'Capacity'
										)}
									</strong>
									<div>{store?.event_capacity || '-'}</div>
								</div>
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields.capacity_overridden ||
												'Capacity Overridden'
										)}
									</strong>
									<div>
										{StringHelpers.title(
											store?.event_capacity
												? fields.yes
												: fields.no || '-'
										)}
									</div>
								</div>
							</div>
							<div className="col-12 col-lg-6 col-xxl-4">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											multiDayEvent ? 
											fields?.start_date || "Start Date" :
											fields?.event_at || "Date"
										)}
									</strong>
									<div>
										{store?.event_at
											? formatDate(store.event_at, DateFormats.DATE)
											: '-'}
									</div>
								</div>
								{multiDayEvent && (
									<div className="d-flex flex-column mb-3">
										<strong>
											{StringHelpers.title(
												fields?.end_date
											)}
										</strong>
										<div>
											{store?.end_date
												? formatDate(store?.end_date, DateFormats.DATE)
												: '-'}
										</div>
									</div>
								)}
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.completed_at || 'Completed'
										)}
									</strong>
									<div>
										{!!store && !!store.completed_at
											? 'Y'
											: 'N'}
									</div>
								</div>
								<div className="d-flex flex-column mb-3">
									<strong>Availability</strong>
									<div>{`${store?.availability_percentage}%`}</div>
								</div>
							</div>
							<div className="col-12 col-lg-6 col-xxl-4">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.created_at || 'Created'
										)}
									</strong>
									<div>
										{store?.created_at
											? formatDate(store.created_at, DateFormats.DATETIME)
											: '-'}
									</div>
								</div>
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.updated_at || 'Updated'
										)}
									</strong>
									<div>
										{store?.updated_at
											? formatDate(store.updated_at, DateFormats.DATETIME)
											: '-'}
									</div>
								</div>
								{!!store?.deleted_at && (
									<div className="d-flex flex-column mb-3">
										<strong>
											{StringHelpers.title(
												fields?.archived_at ||
													'archived'
											)}
										</strong>
										<div>
											{store?.deleted_at
												? formatDate(store.deleted_at, DateFormats.DATETIME)
												: '-'}
										</div>
									</div>
								)}
							</div>
						</div>
						<SubTitle
							title={StringHelpers.title(
								crud?.sub_titles?.payment || 'Payment'
							)}
							className="mb-3"
							style={{ color: '#464C5E' }}
						/>
						<div className="row">
							<div className="col-12 col-lg-6 col-xxl-4">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.requires_payment ||
												'Requires payment'
										)}
									</strong>
									<div>
										{store?.requires_payment === '1'
											? 'Y'
											: 'N'}
									</div>
								</div>
							</div>
							<div className="col-12 col-lg-6 col-xxl-4">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.price || 'Price'
										)}
									</strong>
									<div>{store?.display_price || '-'}</div>
								</div>
							</div>
						</div>
					</CardBody>
				</Card>
				<Card>
					<CardBody>
						<SubTitle
							title={StringHelpers.title(
								fields?.training_venue_id || 'Venue'
							)}
							className="mb-4 text-center"
							style={{ color: '#464C5E' }}
						/>
						<div className="row mb-4">
							<div className="col-12 col-lg-6 col-xxl-4">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.name || 'Name'
										)}
									</strong>
									<div>
										{store?.training_venue?.permissions
											?.view ? (
											<a
												className={'text-dark'}
												href={`/training/venue/${store?.training_venue?.id}`}
												target={'_blank'}
											>
												{StringHelpers.title(
													store?.training_venue
														?.name || '-'
												)}
											</a>
										) : (
											<>
												{StringHelpers.title(
													store?.training_venue
														?.name || '-'
												)}
											</>
										)}
									</div>
								</div>
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.capacity || 'Capacity'
										)}
									</strong>
									<div>
										{store?.training_venue?.capacity ?? '-'}
									</div>
								</div>
							</div>
							<div className="col-12 col-lg-6 col-xxl-4">
								<div
									className="d-flex flex-column mb-3"
									style={{ overflow: 'hidden' }}
								>
									<strong>
										{StringHelpers.title(
											fields?.map_url || 'Map URL'
										)}
									</strong>
									<div>
										{store?.training_venue?.map_url ? (
											<Button
												label={
													crud?.buttons?.map
														?.default || 'Open Map'
												}
												href={
													store.training_venue.map_url
												}
												size={Sizes.Small}
												variant={Variants.Dark}
											/>
										) : (
											'-'
										)}
									</div>
								</div>
							</div>
						</div>
						<SubTitle
							title={StringHelpers.title(
								crud?.sub_titles?.address || 'Address'
							)}
							className="mb-3"
							style={{ color: '#464C5E' }}
						/>
						<div className="row">
							<div className="col-12 col-lg-6 col-xxl-4">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.address_line_1 ||
												'address line 1'
										)}
									</strong>
									<div>
										{StringHelpers.title(
											store?.training_venue?.address
												?.address_line_1 || '-'
										)}
									</div>
								</div>
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.address_line_2 ||
												'address line 2'
										)}
									</strong>
									<div>
										{StringHelpers.title(
											store?.training_venue?.address
												?.address_line_2 || '-'
										)}
									</div>
								</div>
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.address_line_3 ||
												'address line 3'
										)}
									</strong>
									<div>
										{StringHelpers.title(
											store?.training_venue?.address
												?.address_line_3 || '-'
										)}
									</div>
								</div>
							</div>
							<div className="col-12 col-lg-6 col-xxl-4">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.town_city || 'town / city'
										)}
									</strong>
									<div>
										{StringHelpers.title(
											store?.training_venue?.address
												?.town_city || '-'
										)}
									</div>
								</div>
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.state_county ||
												'state / county'
										)}
									</strong>
									<div>
										{StringHelpers.title(
											store?.training_venue?.address
												?.state_county || '-'
										)}
									</div>
								</div>
							</div>
							<div className="col-12 col-lg-6 col-xxl-4">
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.postcode_zipcode ||
												'Postcode / Zipcode'
										)}
									</strong>
									<div>
										{StringHelpers.upper(
											store?.training_venue?.address
												?.postcode_zipcode || '-'
										)}
									</div>
								</div>
								<div className="d-flex flex-column mb-3">
									<strong>
										{StringHelpers.title(
											fields?.country_code_iso_3 ||
												'country'
										)}
									</strong>
									<div>
										{StringHelpers.title(
											store?.training_venue?.address
												?.country || '-'
										)}
									</div>
								</div>
							</div>
						</div>
					</CardBody>
				</Card>
				<EventInvites
					store={store}
					fetchEventData={fetchEventData}
				/>
			</WidthConstrainedContainer>

			<Modal
				open={archiveModalOpen}
				onClose={() => setArchiveModalOpen(false)}
				title={StringHelpers.title(
					replaceKeyWithValue(
						crud?.modals?.archive.title || 'Archive :name?',
						'name',
						crud?.models?.training_event || 'Training Event'
					)
				)}
				confirmText={
					archiving
						? crud?.buttons?.archive?.submitting || 'Archiving...'
						: crud?.buttons?.archive?.default || 'Archive'
				}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				onConfirm={() => setArchiving(true)}
				disabled={disabled || archiving}
				variant={Variants.Danger}
			>
				{crud?.modals?.archive.description.map(
					(item: string, key: number) => {
						return (
							<p key={key}>
								{replaceKeyWithValue(item, 'name', store?.name)}
							</p>
						);
					}
				)}
				<p className="mb-2">
					<strong>
						{StringHelpers.title(
							fields?.description || 'Description'
						)}
						:
					</strong>
					<br />
					{store?.description ? store.description : '-'}
				</p>
				<p className="mb-2">
					<strong>
						{StringHelpers.title(
							checkTheme(Themes.Toshiba)
								? fields?.training_courses || 'Training Courses'
								: fields?.training_course || 'Training Course'
						)}
						:
					</strong>
					<br />
					{store?.training_courses?.length > 0
						? store.training_courses.map((course) =>
							StringHelpers.title(course.name)
						).join(', ')
						: '-'}
				</p>
				<p className="mb-4">
					<strong>
						{StringHelpers.title(fields?.event_at || 'Date')}:
					</strong>
					<br />
					{store?.event_at ? 
						formatDate(store?.event_at, DateFormats.DATE) 
						: '-'
					}
				</p>
				{store?.price_pence !== null && (
					<FormInput
						type="select"
						name="refund_if_paid"
						value={refundIfPaid ? '1' : '0'}
						onChange={(value) => setRefundIfPaid(value === '1')}
						options={crud?.options.boolean || []}
						disabled={disabled || archiving}
						label={StringHelpers.title(
							fields?.refund_if_paid || 'Refund If Paid'
						)}
					/>
				)}
			</Modal>

			<Modal
				open={completeModalOpen}
				onClose={() => setCompleteModalOpen(false)}
				title={StringHelpers.title(
					crud?.buttons?.complete?.default || 'Complete Event'
				)}
				confirmText={StringHelpers.title(
					fields?.yes || 'Yes'
				)}
				closeText={StringHelpers.title(
					fields?.no || 'No'
				)}
				onConfirm={() => {
					setCompleteModalOpen(false);
					navigate(`/training/event/${eventId}/complete`);
				}}
				variant={Variants.Success}
			>
				<p>
					{fields?.complete_modal_description ||
						'Have all attendees been added to this event?'}
				</p>
			</Modal>
	</PageLayout>
	);
}

export default Event;
